// src/features/account/components/StripeConnectButton.jsx
import React, { useState } from 'react';
import { ExternalLink, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { logger } from '../../../utils/logger';

const StripeConnectButton = ({ connectStatus, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAuthToken = () => {
    const userData = JSON.parse(localStorage.getItem('workos_user') || '{}');
    return userData.accessToken;
  };

  const handleOnboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await fetch('/.netlify/functions/stripe-connect-onboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start onboarding');
      }

      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else if (data.status === 'active') {
        // Already connected
        if (onStatusChange) onStatusChange();
      }
    } catch (err) {
      logger.error('Connect onboarding error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await fetch('/.netlify/functions/stripe-connect-dashboard', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get dashboard link');
      }

      if (data.dashboard_url) {
        window.open(data.dashboard_url, '_blank');
      }
    } catch (err) {
      logger.error('Dashboard link error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (connectStatus?.connected && connectStatus?.status === 'active') {
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2 text-green-400">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Stripe Connected</span>
        </div>
        <button
          onClick={handleDashboard}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
          <span>Open Stripe Dashboard</span>
        </button>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>
    );
  }

  if (connectStatus?.connected && connectStatus?.status === 'pending') {
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2 text-yellow-400">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Onboarding Incomplete</span>
        </div>
        <p className="text-gray-400 text-sm">
          Complete your Stripe setup to start receiving payouts.
        </p>
        <button
          onClick={handleOnboard}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
          <span>Complete Setup</span>
        </button>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>
    );
  }

  if (connectStatus?.connected && connectStatus?.status === 'restricted') {
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2 text-yellow-400">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Account Restricted</span>
        </div>
        <p className="text-gray-400 text-sm">
          Your Stripe account needs attention. Please update your information.
        </p>
        <button
          onClick={handleOnboard}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
          <span>Update Account</span>
        </button>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>
    );
  }

  // Not connected
  return (
    <div className="space-y-3">
      <p className="text-gray-400 text-sm">
        Connect your Stripe account to receive payouts for your workflow sales. You'll earn 70% of every sale.
      </p>
      <button
        onClick={handleOnboard}
        disabled={loading}
        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ExternalLink className="w-4 h-4" />
        )}
        <span>Connect with Stripe</span>
      </button>
      {error && <p className="text-red-400 text-sm">{error}</p>}
    </div>
  );
};

export default StripeConnectButton;
